import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import ProfileSectionCard from "./ProfileSectionCard";
import UserService from "services/user.service";
import AuthService from "services/auth.service";
import { FileTextOutlined } from "@ant-design/icons";

const IDVerification = ({ onProfileRefresh, profile, isReadOnly = false }) => {
  const [idType, setIdType] = useState("");
  const [idNumber, setIdNumber] = useState("");

  const [idImageFrontFile, setIdImageFrontFile] = useState(null);
  const [isIdImageFrontEmpty, setIsIdImageFrontEmpty] = useState(false);

  const [idImageBackFile, setIdImageBackFile] = useState(null);
  const [isIdImageBackEmpty, setIsIdImageBackEmpty] = useState(false);

  const idImageFront = profile?.id_image_front;
  const idImageFrontUrl = idImageFront?.styles?.origin?.url;
  const idImageFrontFilename = idImageFront?.original_filename ||
    (idImageFrontUrl
      ? idImageFrontUrl.split('/').pop().split('?')[0]
      : "View ID Image (Front)"
    );

  const idImageBack = profile?.id_image_back;
  const idImageBackUrl = idImageBack?.styles?.origin?.url;
  const idImageBackFilename = idImageBack?.original_filename ||
    (idImageBackUrl
      ? idImageBackUrl.split('/').pop().split('?')[0]
      : "View ID Image (Back)"
    );
  
  const isPendingReview = profile?.pending_sections?.identity;

  const resetForm = () => {
    setIdType(profile?.identification_type || "NRIC");
    setIdNumber(profile?.nric || "");
    setIdImageFrontFile(null);
    setIsIdImageFrontEmpty(false);
    setIdImageBackFile(null);
    setIsIdImageBackEmpty(false);
  };

  useEffect(() => {
    resetForm();
  }, [profile]);

  const handleBeforeEdit = () => {
    if (isPendingReview) {
      toast.warning("You already have an edit pending admin review. Please wait for approval.");
      return false;
    }
    return true;
  };

  const validateInput = () => {
    if (!idNumber.trim()) {
      toast.error("Identification Number cannot be empty.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateInput()) {
      throw new Error("Validation failed");
    }

    const initialIdType = profile?.identification_type || "NRIC";
    const initialIdNumber = profile?.nric || "";

    const fileChanged =
      idImageFrontFile !== null ||
      isIdImageFrontEmpty ||
      idImageBackFile !== null ||
      isIdImageBackEmpty;

    const isChanged =
      fileChanged ||
      idType !== initialIdType ||
      idNumber.trim() !== initialIdNumber.trim();

    if (!isChanged) {
      resetForm();
      return;
    }

    const formData = new FormData();
   
    formData.append("request_type", "identity");
    formData.append("attributes[identification_type]", idType);
    formData.append("attributes[nric]", idNumber.trim());

    if (idImageFrontFile) {
      formData.append("files[id_image_front]", idImageFrontFile);
    } else if (isIdImageFrontEmpty) {
      formData.append("attributes[remove_id_image_front_url]", "1");
    }

    if (idImageBackFile) {
      formData.append("files[id_image_back]", idImageBackFile);
    } else if (isIdImageBackEmpty) {
      formData.append("attributes[remove_id_image_back_url]", "1");
    }

    try {
      await UserService.submitProfileChangeRequest(formData);
      toast.success("Changes saved and submitted for admin approval!");
      if (onProfileRefresh) {
        await onProfileRefresh();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        AuthService.logout();
        window.location.replace("/eb/login");
      } else {
        toast.error(
          error.response?.data?.errors?.join(",") || "Failed to submit changes."
        );
      }
      throw error;
    }
  };

  // File Change Handlers
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateAndSetFile = (file, setFileMethod, setEmptyMethod) => {
    if (file) {
      if (file.size > 4 * 1024 * 1024) {
        toast.error("File size should not exceed 4MB.");
        return;
      }
      setFileMethod(file);
      setEmptyMethod(false);
    }
  };

  // === ID Image Handlers ===
  const handleIdImageFrontChange = (e) => {
    validateAndSetFile(e.target.files[0], setIdImageFrontFile, setIsIdImageFrontEmpty);
  };
  const handleClearIdImageFront = () => {
    setIdImageFrontFile(null);
    setIsIdImageFrontEmpty(true);
  };

  const handleIdImageBackChange = (e) => {
    validateAndSetFile(e.target.files[0], setIdImageBackFile, setIsIdImageBackEmpty);
  };
  const handleClearIdImageBack = () => {
    setIdImageBackFile(null);
    setIsIdImageBackEmpty(true);
  };

  // === Display Variables ===
  const hasIdImageFrontPresent = !!idImageFrontFile || (!!idImageFrontUrl && !isIdImageFrontEmpty);
  const displayIdImageFrontFileName = idImageFrontFile
    ? idImageFrontFile.name
    : hasIdImageFrontPresent
    ? idImageFrontFilename
    : null;
  const displayIdImageFrontFileSize = idImageFrontFile
    ? formatFileSize(idImageFrontFile.size)
    : (hasIdImageFrontPresent && idImageFront?.file_size)
      ? formatFileSize(idImageFront.file_size)
      : "PDF, JPG, JPEG, PNG (Max. 4MB)";

  const hasIdImageBackPresent = !!idImageBackFile || (!!idImageBackUrl && !isIdImageBackEmpty);
  const displayIdImageBackFileName = idImageBackFile
    ? idImageBackFile.name
    : hasIdImageBackPresent
    ? idImageBackFilename
    : null;
  const displayIdImageBackFileSize = idImageBackFile
    ? formatFileSize(idImageBackFile.size)
    : (hasIdImageBackPresent && idImageBack?.file_size)
      ? formatFileSize(idImageBack.file_size)
      : "PDF, JPG, JPEG, PNG (Max. 4MB)";

  const formatIdType = (type) => {
    if (!type) return "-";
    if (type.toLowerCase() === "nric") return "NRIC";
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  };

  return (
    <ProfileSectionCard
      title="ID Verification"
      showEditButton={!isReadOnly}
      onBeforeEdit={handleBeforeEdit}
      onSave={handleSave}
      onCancel={resetForm}
    >
      {({ isEditing }) => (
        <div className="profile-info-group">
          <p className="text-muted mb-6">To verify your identity and ensure compliance with safety and legal requirements.</p>
          {/* READ ONLY MODE */}
          {!isEditing && (
            <>
              <div className="profile-info-row mb-4">
                <h6 className="profile-info-label">Identification Type</h6>
                <p className="profile-info-value">{formatIdType(idType)}</p>
              </div>
              <div className="profile-info-row mb-4">
                <h6 className="profile-info-label">Identification Number</h6>
                <p className="profile-info-value">{idNumber || "-"}</p>
              </div>

              <div className="profile-info-row mb-3">
                <h6 className="profile-info-label">ID Image (Front)</h6>
                <p className="profile-info-value">
                  {idImageFrontUrl ? (
                    <a
                      href={idImageFrontUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="photo-link"
                    >
                      {idImageFrontFilename}
                    </a>
                  ) : (
                    <span className="text-muted">No file uploaded</span>
                  )}
                </p>
              </div>

              <div className="profile-info-row">
                <h6 className="profile-info-label">ID Image (Back)</h6>
                <p className="profile-info-value">
                  {idImageBackUrl ? (
                    <a
                      href={idImageBackUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="photo-link"
                    >
                      {idImageBackFilename}
                    </a>
                  ) : (
                    <span className="text-muted">No file uploaded</span>
                  )}
                </p>
              </div>
            </>
          )}

          {/* EDIT MODE */}
          {isEditing && (
            <div className="profile-info-group">
              <div className="profile-info-row mb-4">
                <label className="profile-info-label">Identification Type</label>
                <select
                  className="form-control py-2 px-3 profile-info-value"
                  value={idType}
                  onChange={(e) => setIdType(e.target.value)}
                >
                  <option value="nric">NRIC</option>
                  <option value="passport">Passport</option>
                </select>
              </div>
              <div className="profile-info-row mb-4">
                <label className="profile-info-label">Identification Number</label>
                <input
                  type="text"
                  className="form-control py-6 px-4 profile-info-value"
                  value={idNumber}
                  placeholder="e.g. 900101-14-5123 or A12345678"
                  onChange={(e) => setIdNumber(e.target.value)}
                />
              </div>

              {/* Upload ID Image Front */}
              <div className="profile-info-row mb-4">
                <label className="profile-info-label">ID Image (Front)</label>
                <div className="profile-info-value">
                  <div className="file-upload-box mb-3">
                    <div className="d-flex align-items-center">
                      {hasIdImageFrontPresent && (
                        <div className="me-3" style={{ marginRight: "16px" }}>
                          <FileTextOutlined className="text-danger" style={{ fontSize: "24px" }}/>
                        </div>
                      )}
                      <div>
                        <div className="text-dark font-weight-medium">
                          {displayIdImageFrontFileName
                            ? displayIdImageFrontFileName
                            : "Upload your file here"}
                        </div>
                        <small className="text-muted">
                          {displayIdImageFrontFileSize}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label
                        className={`mb-0 cursor-pointer ${hasIdImageFrontPresent ? "replace-file-btn" : "browse-file-btn"}`}
                      >
                        {hasIdImageFrontPresent ? "Replace" : "Browse File"}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="d-none"
                          onChange={handleIdImageFrontChange}
                          onClick={(e) => {
                            e.target.value = null;
                          }}
                        />
                      </label>
                      {hasIdImageFrontPresent && (
                        <button
                          type="button"
                          className="clear-file-btn"
                          style={{ paddingLeft: "15px" }}
                          onClick={handleClearIdImageFront}
                          title="Remove file"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload ID Image Back */}
              <div className="profile-info-row">
                <label className="profile-info-label">ID Image (Back)</label>
                <div className="profile-info-value">
                  <div className="file-upload-box mb-3">
                    <div className="d-flex align-items-center">
                      {hasIdImageBackPresent && (
                        <div className="me-3" style={{ marginRight: "16px" }}>
                          <FileTextOutlined className="text-danger" style={{ fontSize: "24px" }}/>
                        </div>
                      )}
                      <div>
                        <div className="text-dark font-weight-medium">
                          {displayIdImageBackFileName ? displayIdImageBackFileName : "Upload your file here"}
                        </div>
                        <small className="text-muted">
                          {displayIdImageBackFileSize}
                        </small>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <label
                        className={`mb-0 cursor-pointer ${hasIdImageBackPresent ? "replace-file-btn" : "browse-file-btn"}`}
                      >
                        {hasIdImageBackPresent ? "Replace" : "Browse File"}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          className="d-none"
                          onChange={handleIdImageBackChange}
                          onClick={(e) => {
                            e.target.value = null;
                          }}
                        />
                      </label>
                      {hasIdImageBackPresent && (
                        <button
                          type="button"
                          className="clear-file-btn"
                          style={{ paddingLeft: "15px" }}
                          onClick={handleClearIdImageBack}
                          title="Remove file"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </ProfileSectionCard>
  );
};

export default IDVerification;